export interface KPIData {
  title: string
  value: string
  trend: number
  trendLabel: string
  type: 'currency' | 'number' | 'percentage'
}

export interface ChannelData {
  name: string
  pos: number
  softPos: number
  mobilePos: number
  eCommerce: number
  raastQr: number
  raastRtp: number
}

export interface TrendData {
  time: string
  currentValue: number
  previousValue: number
  currentCount: number
  previousCount: number
}

export interface SchemeData {
  name: string
  count: number
  value: number
  percentage: number
  color: string
}

export interface BranchData {
  id: string
  name: string
  transactions: number
  salesValue: number
  avgTicket: number
}

export interface TerminalData {
  id: string
  type: 'POS' | 'SoftPOS' | 'MobilePOS'
  transactions: number
  salesValue: number
  successRate: number
}

export interface Transaction {
  id: string
  dateTime: string
  channel: string
  scheme: string
  amount: number
  status: 'Success' | 'Failed' | 'Reversed' | 'Pending'
  terminalId: string
}

export interface SettlementData {
  today: number
  upcoming: number
  lastDate: string
  monthTotal: number
}

export interface Alert {
  id: string
  type: 'success' | 'warning' | 'error' | 'info'
  title: string
  message: string
  time: string
}

export const getKPIData = (): KPIData[] => [
  {
    title: 'Total Sales Value',
    value: '2,847,350',
    trend: 12.5,
    trendLabel: 'vs previous period',
    type: 'currency',
  },
  {
    title: 'Total Transactions',
    value: '1,247',
    trend: 8.3,
    trendLabel: 'vs previous period',
    type: 'number',
  },
  {
    title: 'Average Transaction Value',
    value: '2,283',
    trend: -2.1,
    trendLabel: 'vs previous period',
    type: 'currency',
  },
  {
    title: 'Success Rate',
    value: '97.8',
    trend: 0.5,
    trendLabel: 'vs previous period',
    type: 'percentage',
  },
  {
    title: 'Settlement Amount',
    value: '2,156,000',
    trend: 5.2,
    trendLabel: 'vs previous period',
    type: 'currency',
  },
  {
    title: 'Pending Settlement',
    value: '691,350',
    trend: 1.1,
    trendLabel: 'vs previous period',
    type: 'currency',
  },
]

export const getChannelData = (): ChannelData[] => [
  {
    name: 'Mon',
    pos: 400000,
    softPos: 150000,
    mobilePos: 100000,
    eCommerce: 300000,
    raastQr: 50000,
    raastRtp: 80000,
  },
  {
    name: 'Tue',
    pos: 380000,
    softPos: 160000,
    mobilePos: 110000,
    eCommerce: 320000,
    raastQr: 55000,
    raastRtp: 85000,
  },
  {
    name: 'Wed',
    pos: 420000,
    softPos: 140000,
    mobilePos: 105000,
    eCommerce: 310000,
    raastQr: 60000,
    raastRtp: 90000,
  },
  {
    name: 'Thu',
    pos: 450000,
    softPos: 170000,
    mobilePos: 120000,
    eCommerce: 350000,
    raastQr: 65000,
    raastRtp: 95000,
  },
  {
    name: 'Fri',
    pos: 500000,
    softPos: 180000,
    mobilePos: 130000,
    eCommerce: 400000,
    raastQr: 70000,
    raastRtp: 100000,
  },
  {
    name: 'Sat',
    pos: 600000,
    softPos: 200000,
    mobilePos: 150000,
    eCommerce: 450000,
    raastQr: 80000,
    raastRtp: 110000,
  },
  {
    name: 'Sun',
    pos: 550000,
    softPos: 190000,
    mobilePos: 140000,
    eCommerce: 420000,
    raastQr: 75000,
    raastRtp: 105000,
  },
]

export const getTrendData = (): TrendData[] => [
  {
    time: '00:00',
    currentValue: 12000,
    previousValue: 10000,
    currentCount: 15,
    previousCount: 12,
  },
  {
    time: '04:00',
    currentValue: 8000,
    previousValue: 9000,
    currentCount: 10,
    previousCount: 11,
  },
  {
    time: '08:00',
    currentValue: 45000,
    previousValue: 38000,
    currentCount: 55,
    previousCount: 48,
  },
  {
    time: '12:00',
    currentValue: 125000,
    previousValue: 110000,
    currentCount: 140,
    previousCount: 125,
  },
  {
    time: '16:00',
    currentValue: 150000,
    previousValue: 135000,
    currentCount: 180,
    previousCount: 160,
  },
  {
    time: '20:00',
    currentValue: 180000,
    previousValue: 160000,
    currentCount: 210,
    previousCount: 190,
  },
  {
    time: '24:00',
    currentValue: 95000,
    previousValue: 85000,
    currentCount: 110,
    previousCount: 100,
  },
]

export const getSchemeData = (): SchemeData[] => [
  {
    name: 'Visa',
    count: 520,
    value: 1250000,
    percentage: 43.9,
    color: '#1A1F71',
  },
  {
    name: 'MasterCard',
    count: 410,
    value: 980000,
    percentage: 34.4,
    color: '#EB001B',
  },
  {
    name: 'UnionPay',
    count: 125,
    value: 280000,
    percentage: 9.8,
    color: '#D50032',
  },
  {
    name: 'PayPak',
    count: 95,
    value: 185000,
    percentage: 6.5,
    color: '#00A651',
  },
  {
    name: 'RAAST',
    count: 97,
    value: 152350,
    percentage: 5.4,
    color: '#00B0F0',
  },
]

export const getBranchData = (): BranchData[] => [
  {
    id: 'B001',
    name: 'Main Branch Karachi',
    transactions: 450,
    salesValue: 1150000,
    avgTicket: 2555,
  },
  {
    id: 'B002',
    name: 'DHA Outlet',
    transactions: 320,
    salesValue: 850000,
    avgTicket: 2656,
  },
  {
    id: 'B003',
    name: 'Clifton Branch',
    transactions: 280,
    salesValue: 620000,
    avgTicket: 2214,
  },
  {
    id: 'B004',
    name: 'Gulshan Store',
    transactions: 150,
    salesValue: 180000,
    avgTicket: 1200,
  },
  {
    id: 'B005',
    name: 'Tariq Road',
    transactions: 47,
    salesValue: 47350,
    avgTicket: 1007,
  },
]

export const getTerminalData = (): TerminalData[] => [
  {
    id: 'T-8472',
    type: 'POS',
    transactions: 180,
    salesValue: 450000,
    successRate: 98.5,
  },
  {
    id: 'T-9231',
    type: 'SoftPOS',
    transactions: 145,
    salesValue: 320000,
    successRate: 99.1,
  },
  {
    id: 'T-1045',
    type: 'MobilePOS',
    transactions: 95,
    salesValue: 185000,
    successRate: 96.2,
  },
  {
    id: 'T-8473',
    type: 'POS',
    transactions: 160,
    salesValue: 410000,
    successRate: 97.8,
  },
  {
    id: 'T-9232',
    type: 'SoftPOS',
    transactions: 120,
    salesValue: 280000,
    successRate: 98.9,
  },
  {
    id: 'T-1046',
    type: 'MobilePOS',
    transactions: 85,
    salesValue: 160000,
    successRate: 95.5,
  },
]

export const getRecentTransactions = (): Transaction[] => [
  {
    id: 'TX-982374',
    dateTime: '2023-10-25 14:32:11',
    channel: 'POS',
    scheme: 'Visa',
    amount: 4500,
    status: 'Success',
    terminalId: 'T-8472',
  },
  {
    id: 'TX-982373',
    dateTime: '2023-10-25 14:28:45',
    channel: 'E-Commerce',
    scheme: 'MasterCard',
    amount: 12500,
    status: 'Success',
    terminalId: 'Web',
  },
  {
    id: 'TX-982372',
    dateTime: '2023-10-25 14:15:22',
    channel: 'SoftPOS',
    scheme: 'UnionPay',
    amount: 3200,
    status: 'Failed',
    terminalId: 'T-9231',
  },
  {
    id: 'TX-982371',
    dateTime: '2023-10-25 14:05:10',
    channel: 'RAAST QR',
    scheme: 'RAAST',
    amount: 1500,
    status: 'Success',
    terminalId: 'QR-01',
  },
  {
    id: 'TX-982370',
    dateTime: '2023-10-25 13:55:33',
    channel: 'POS',
    scheme: 'PayPak',
    amount: 850,
    status: 'Success',
    terminalId: 'T-8473',
  },
  {
    id: 'TX-982369',
    dateTime: '2023-10-25 13:42:18',
    channel: 'MobilePOS',
    scheme: 'Visa',
    amount: 5600,
    status: 'Pending',
    terminalId: 'T-1045',
  },
  {
    id: 'TX-982368',
    dateTime: '2023-10-25 13:30:05',
    channel: 'E-Commerce',
    scheme: 'MasterCard',
    amount: 24000,
    status: 'Reversed',
    terminalId: 'Web',
  },
]

export const getSettlementData = (): SettlementData => ({
  today: 450000,
  upcoming: 691350,
  lastDate: '2023-10-24',
  monthTotal: 12450000,
})

export const getAlerts = (): Alert[] => [
  {
    id: 'A1',
    type: 'success',
    title: 'Settlement Completed',
    message: '₨450,000 settled to your account ending in 4582.',
    time: '10 mins ago',
  },
  {
    id: 'A2',
    type: 'warning',
    title: 'High Failure Rate',
    message:
      'Terminal T-1045 is experiencing a 15% failure rate in the last hour.',
    time: '1 hour ago',
  },
  {
    id: 'A3',
    type: 'error',
    title: 'Chargeback Received',
    message: 'New chargeback of ₨12,500 on TX-981245. Action required.',
    time: '3 hours ago',
  },
  {
    id: 'A4',
    type: 'info',
    title: 'New Feature',
    message: 'RAAST RTP is now available for all your branches.',
    time: '1 day ago',
  },
  {
    id: 'A5',
    type: 'warning',
    title: 'Terminal Offline',
    message: 'Terminal T-8472 at Gulshan Store has been offline for 4 hours.',
    time: '1 day ago',
  },
]
